import {
	ActionIcon,
	Avatar,
	Badge,
	Button,
	Card,
	Flex,
	Grid,
	Group,
	Menu,
	Modal,
	Select,
	Stack,
	Text,
	TextInput,
	Textarea,
} from '@mantine/core'
import {
	IconBan,
	IconCheck,
	IconCircleDot,
	IconDotsVertical,
	IconEye,
	IconMail,
	IconPencil,
	IconPlayerPlay,
	IconTrash,
	IconX,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	blockReportTarget,
	deleteReportContent,
	getListReport,
	getReportStats,
	rejectReport,
	replyReportEmail,
	updateReportNote,
	updateReportStatus,
} from 'src/apis/reportAPI'
import {
	getSubmittedMedia,
	getTargetMedia,
	getTargetPreviewMediaUrl,
	SubmittedMedia,
} from './reportMedia'
import {
	canDeleteReportContent,
	getReportContentDeletedAt,
	getReportContentTypeLabel,
	getTargetName,
	getReportTargetScope,
	getReportTargetScopeLabel,
	isReportContentDeleted,
} from './reportTarget'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import TableComponent from 'src/components/TableComponent'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { color, formatDateTime } from 'src/variable/constant'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'

const STATUS_META = {
	pending: {
		label: 'New',
		color: 'violet',
		background: '#f0e7ff',
		text: '#5b21b6',
	},
	in_review: {
		label: 'Following',
		color: 'yellow',
		background: '#fff3cd',
		text: '#b45309',
	},
	resolved: {
		label: 'Done',
		color: 'green',
		background: '#dcfce7',
		text: '#15803d',
	},
	rejected: {
		label: 'Rejected',
		color: 'gray',
		background: '#eef2f7',
		text: '#475569',
	},
}

const STATUS_OPTIONS = [
	{ label: 'All', value: 'all' },
	{ label: 'New', value: 'pending' },
	{ label: 'Following', value: 'in_review' },
	{ label: 'Done', value: 'resolved' },
	{ label: 'Rejected', value: 'rejected' },
]

const REPORT_STATUS_VALUES = STATUS_OPTIONS
	.map((option) => option.value)
	.filter((value) => value !== 'all')

const ISSUE_OPTIONS = [
	{ label: 'All', value: 'all' },
	{ label: 'Rude or disrespect', value: 'rude_or_disrespect' },
	{ label: 'Fake or outside payment', value: 'fake_or_outside_payment' },
	{ label: 'Spam messages', value: 'spam_messages' },
	{ label: 'No reply after order', value: 'no_reply_after_order' },
	{ label: 'Abuse refund system', value: 'abuse_refund_system' },
	{ label: 'Scam fraud', value: 'scam_fraud' },
	{ label: 'Misleading info', value: 'misleading_info' },
	{ label: 'Safety issues', value: 'safety_issues' },
	{ label: 'Poor services', value: 'poor_services' },
	{ label: 'No show cancel', value: 'no_show_cancel' },
	{ label: 'Inappropriate content', value: 'inappropriate_content' },
	{ label: 'Harassment abuse', value: 'harassment_abuse' },
	{ label: 'Spam promotion', value: 'spam_promotion' },
	{ label: 'Illegal or restricted service', value: 'illegal_or_restricted_service' },
	{ label: 'Poor or unsafe service', value: 'poor_or_unsafe_service' },
	{ label: 'Copied stolen content', value: 'copied_stolen_content' },
	{ label: 'Wrong irrelevant content', value: 'wrong_irrelevant_content' },
	{ label: 'Language errors', value: 'language_errors' },
	{ label: 'Formatting issues', value: 'formatting_issues' },
	{ label: 'Text not showing correctly', value: 'text_not_showing_correctly' },
	{ label: 'Audio does not work', value: 'audio_doesnt_work' },
	{ label: 'Progress is not saved', value: 'progress_is_not_saved' },
	{ label: 'Incorrect score', value: 'incorrect_score' },
	{ label: 'Misunderstood words', value: 'misunderstood_words' },
	{ label: 'Unhelpful feedback', value: 'unhelpful_feedback' },
	{ label: 'Uninteresting exercise', value: 'uninteresting_exercise' },
	{ label: 'Wrong correction', value: 'wrong_correction' },
]

const SORT_OPTIONS = [
	{ label: 'Newest', value: 'newest' },
	{ label: 'Oldest', value: 'oldest' },
	{ label: 'Updated newest', value: 'updated_newest' },
	{ label: 'Updated oldest', value: 'updated_oldest' },
]

const SEARCH_FIELD_OPTIONS = [
	{ label: 'Reporter name', value: 'reporter_name' },
	{ label: 'Reporter email', value: 'reporter_email' },
	{ label: 'Target name', value: 'target_name' },
	{ label: 'Ticket ID', value: 'ticket_id' },
	{ label: 'Issue type', value: 'issue_type' },
	{ label: 'Content', value: 'content' },
]

const SEARCH_PLACEHOLDER_MAP = {
	reporter_name: 'Search reporter name...',
	reporter_email: 'Search email...',
	target_name: 'Search target name...',
	ticket_id: 'Search ticket ID...',
	issue_type: 'Search issue type...',
	content: 'Search content...',
}

const normalizeTicketSearchValue = (value?: string) => {
	return String(value || '')
		.trim()
		.replace(/^#?RPT-/i, '')
}

const BLOCK_TYPE_OPTIONS = [
	{ label: 'Permanent', value: 'FOREVER' },
	{ label: 'One day', value: 'ONE_DAY' },
	{ label: 'Three days', value: 'THREE_DAYS' },
]

const ACTION_COLUMN_WIDTH = 120
const SEND_EMAIL_COLUMN_WIDTH = 140
const FILTER_SELECT_DROPDOWN_PROPS = {
	withinPortal: true,
	dropdownPosition: 'bottom' as const,
	zIndex: 1000,
}

const getPinnedReportColumnProps = (right: number, isLeftEdge = false) => ({
	sx: {
		position: 'sticky' as const,
		right,
		zIndex: 4,
		background: '#ffffff',
		borderLeft: isLeftEdge ? '1px solid #e2e8f0' : undefined,
		boxShadow: isLeftEdge
			? '-14px 0 24px -20px rgba(15, 23, 42, 0.45)'
			: undefined,
	},
})

const getPinnedReportHeadColumnProps = (right: number, isLeftEdge = false) => ({
	sx: {
		...getPinnedReportColumnProps(right, isLeftEdge).sx,
		zIndex: 5,
		background: '#f7faf7',
	},
})

const humanize = (value?: string) => {
	if (!value) return '-'
	return value
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (char) => char.toUpperCase())
}

const getStatusMeta = (status?: string) => {
	return STATUS_META[status] || STATUS_META.pending
}

const getScopeColor = (scope?: string) => {
	if (scope === 'COMMUNITY') return 'grape'
	if (scope === 'EVENT' || scope === 'HANGOUT') return 'orange'
	if (scope === 'COURSE' || scope === 'COURSE_AI') return 'indigo'
	if (scope === 'TUTOR') return 'grape'
	if (scope === 'SERVICE') return 'teal'
	if (scope === 'LMB') return 'cyan'
	if (scope === 'LIBRARY') return 'violet'
	return 'gray'
}

const getTargetSubtitle = (report: any) => {
	const target = report?.target || {}
	return (
		target?.username ||
		target?.phone ||
		target?.business_email ||
		target?.business_phone ||
		target?.author ||
		report?.report_target_id ||
		'-'
	)
}

const getTargetRoute = (report: any) => {
	if (report?.report_type === 'buyer' && report?.report_target_id) {
		return `/users/${report.report_target_id}`
	}

	return ''
}

const buildWebUrl = (path: string) => {
	const webUrl = process.env.REACT_APP_WEB_URL?.replace(/\/+$/, '')
	if (!webUrl || !path) return ''

	return `${webUrl}${path.startsWith('/') ? path : `/${path}`}`
}

const getPostWebPath = (post: any, fallbackId?: string) => {
	const postId = post?.id || fallbackId
	const postType = `${post?.type || ''}`.toUpperCase()

	if (!postId) return ''

	if (['DISCUSS_IN_TOPIC'].includes(postType)) {
		return `/en/discussion?id=${encodeURIComponent(postId)}`
	}

	if (['DISCUSS_IN_CLUB'].includes(postType)) {
		if (!post?.conversation_id) return ''

		return `/en/community/${encodeURIComponent(post.conversation_id)}?id=${encodeURIComponent(postId)}`
	}

	if (
		['EVENT', 'COURSE', 'COURSE_UNIVINI', 'COURSE_OF_LEANER', 'COURSE_OF_TUTOR'].includes(
			postType,
		)
	) {
		return `/en/public-event/${encodeURIComponent(postId)}`
	}

	return ''
}

const getTargetWebUrl = (report: any) => {
	const target = report?.target || {}
	const reportType = `${report?.report_type || ''}`.toUpperCase()
	const targetId = report?.report_target_id

	if ((reportType === 'USER' || reportType === 'BUYER') && targetId) {
		return buildWebUrl(`/en/profile/${encodeURIComponent(targetId)}`)
	}

	if (reportType === 'COMMENT') {
		const parentPost = target?.post || target?.parent_post || {}
		return buildWebUrl(getPostWebPath(parentPost, target?.post_id))
	}

	if (reportType === 'POST' || reportType === 'EVENT') {
		return buildWebUrl(getPostWebPath(target, targetId))
	}

	return ''
}

const shortId = (id?: string) => {
	if (!id) return '-'
	return `#RPT-${id.slice(0, 8).toUpperCase()}`
}

const TruncatedText = ({ value, maxWidth = 260 }) => (
	<CustomTooltip
		label={value || '-'}
		sx={{
			maxWidth: 460,
			whiteSpace: 'normal',
			padding: 10,
			wordBreak: 'break-word',
		}}
	>
		<div
			style={{
				maxWidth,
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				display: '-webkit-box',
				WebkitLineClamp: 2,
				WebkitBoxOrient: 'vertical',
				whiteSpace: 'normal',
			}}
		>
			{value || '-'}
		</div>
	</CustomTooltip>
)

const getAvatarLabel = (title?: string) => {
	const value = title?.trim()
	if (!value || value === '-') return '?'
	return value.charAt(0).toUpperCase()
}

const DetailSection = ({ title, children }) => (
	<Card withBorder radius="md" p="md">
		<Stack spacing={10}>
			<Text fw={800} size="sm" color="#0f172a">
				{title}
			</Text>
			{children}
		</Stack>
	</Card>
)

const DetailRow = ({ label, value }) => (
	<Text size="sm" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
		<Text span fw={700}>{label}: </Text>
		{value === 0 ? 0 : value || '-'}
	</Text>
)

const LinkedText = ({ title, subtitle, to, avatar }) => {
	const navigate = useNavigate()
	return (
		<Group spacing={10} noWrap>
			<Avatar src={avatar || undefined} radius="xl" size={36} color="gray">
				{getAvatarLabel(title)}
			</Avatar>
			<Stack spacing={2}>
				<Text
					fw={700}
					size="sm"
					color={to ? color.Blue : '#0f172a'}
					sx={{ cursor: to ? 'pointer' : 'default' }}
					onClick={() => to && navigate(to)}
				>
					{title || '-'}
				</Text>
				<Text size="xs" color="dimmed">
					{subtitle || '-'}
				</Text>
			</Stack>
		</Group>
	)
}

function ReportPage() {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const didMountFilter = useRef(false)
	const [data, setData] = useState<any[]>([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [detailReport, setDetailReport] = useState<any>(null)
	const [replyReport, setReplyReport] = useState<any>(null)
	const [blockReport, setBlockReport] = useState<any>(null)
	const [noteReport, setNoteReport] = useState<any>(null)
	const [previewMedia, setPreviewMedia] = useState<SubmittedMedia | null>(null)
	const [replyForm, setReplyForm] = useState({ subject: '', body: '' })
	const [noteForm, setNoteForm] = useState({ note: '' })
	const [blockForm, setBlockForm] = useState({
		reason: '',
		type: 'FOREVER',
		target_type: 'target' as 'target' | 'reporter',
	})
	const [stats, setStats] = useState({
		pending: 0,
		in_review: 0,
		resolved: 0,
		rejected: 0,
	})
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [filter, setFilter] = useState({
		search_field: 'reporter_name',
		search_value: '',
		status: 'all',
		issue_type: 'all',
		sort_by: 'newest',
	})

	const fetchStats = useCallback(async () => {
		try {
			const res = await getReportStats()
			setStats({
				pending: Number(res?.data?.results?.object?.pending || 0),
				in_review: Number(res?.data?.results?.object?.in_review || 0),
				resolved: Number(res?.data?.results?.object?.resolved || 0),
				rejected: Number(res?.data?.results?.object?.rejected || 0),
			})
		} catch (error) {
			openError(error)
		}
	}, [openError])

	const fetchReports = useCallback(async () => {
		setLoading(true)
		try {
			const searchValue =
				filter.search_field === 'ticket_id'
					? normalizeTicketSearchValue(filter.search_value)
					: filter.search_value?.trim()
			const res = await getListReport({
				page: pagination.pageIndex,
				limit: pagination.pageSize,
				field: searchValue ? filter.search_field : undefined,
				value: searchValue || undefined,
				status: filter.status === 'all' ? undefined : filter.status,
				issue_type: filter.issue_type === 'all' ? undefined : filter.issue_type,
				sort_by: filter.sort_by,
			})
			const rows = res?.data?.results?.objects?.rows || []
			setData(rows)
			setTotal(res?.data?.results?.objects?.count || 0)
		} catch (error) {
			openError(error)
		} finally {
			setLoading(false)
		}
	}, [filter, openError, pagination])

	const refreshData = useCallback(async () => {
		await Promise.all([fetchReports(), fetchStats()])
	}, [fetchReports, fetchStats])

	useEffect(() => {
		fetchStats()
	}, [fetchStats])

	useEffect(() => {
		fetchReports()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(pagination)])

	useEffect(() => {
		if (!didMountFilter.current) {
			didMountFilter.current = true
			return
		}
		const timer = setTimeout(() => {
			if (pagination.pageIndex === 1) {
				fetchReports()
			} else {
				setPagination((prev) => ({ ...prev, pageIndex: 1 }))
			}
		}, 400)

		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(filter)])

	const handleChangePage = useCallback((value: number) => {
		setPagination((prev) => ({
			...prev,
			pageIndex: value,
		}))
	}, [])

	const handleFilterQueryInit = useCallback((query: Record<string, string>) => {
		const nextFilter = {
			search_field: SEARCH_FIELD_OPTIONS.some(
				(option) => option.value === query.search_field,
			)
				? query.search_field
				: 'reporter_name',
			status: STATUS_OPTIONS.some((option) => option.value === query.status)
				? query.status
				: 'all',
			issue_type: ISSUE_OPTIONS.some((option) => option.value === query.issue_type)
				? query.issue_type
				: 'all',
			sort_by: SORT_OPTIONS.some((option) => option.value === query.sort_by)
				? query.sort_by
				: 'newest',
		}

		setFilter((prev) => ({
			...prev,
			...nextFilter,
		}))
	}, [])

	const handleStatus = useCallback(
		async (report: any, status: string) => {
			if (!report?.id) {
				openModal({ type: 'error', label: 'Report ID is missing' })
				return
			}
			if (!REPORT_STATUS_VALUES.includes(status)) {
				openModal({ type: 'error', label: 'Report status is invalid' })
				return
			}
			openLoadingContext()
			try {
				const res = await updateReportStatus(report.id, status)
				const updatedStatus =
					res?.data?.results?.object?.status ||
					res?.data?.results?.objects?.status ||
					status
				setData((prev) => {
					if (filter.status !== 'all' && filter.status !== updatedStatus) {
						return prev.filter((item) => item.id !== report.id)
					}
					return prev.map((item) =>
						item.id === report.id
							? {
									...item,
									status: updatedStatus,
									last_action: {
										...(item.last_action || {}),
										type: 'status_update',
										status: updatedStatus,
									},
							  }
							: item,
					)
				})
				await refreshData()
				openModal({ type: 'success', label: 'Report status updated' })
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
			[
				closeLoadingContext,
				filter.status,
				openError,
				openLoadingContext,
				openModal,
				refreshData,
			],
	)

	const handleReject = useCallback(
		(report: any) => {
			if (!report?.id) {
				openModal({ type: 'error', label: 'Report ID is missing' })
				return
			}
			openModal({
				type: 'confirm',
				label: <div>Do you want to reject this report?</div>,
				handleConfirm: async () => {
					openLoadingContext()
					try {
						await rejectReport(report.id)
						await refreshData()
						openModal({ type: 'success', label: 'Report rejected' })
					} catch (error) {
						openError(error)
					} finally {
						closeLoadingContext()
					}
				},
			})
		},
		[closeLoadingContext, openError, openLoadingContext, openModal, refreshData],
	)

	const handleDeleteContent = useCallback(
		(report: any) => {
			if (!report?.id) {
				openModal({ type: 'error', label: 'Report ID is missing' })
				return
			}
			openModal({
				type: 'confirm',
				label: <div>Do you want to delete the reported content?</div>,
				handleConfirm: async () => {
					openLoadingContext()
					try {
						await deleteReportContent(report.id)
						await refreshData()
						openModal({ type: 'success', label: 'Reported content deleted' })
					} catch (error) {
						openError(error)
					} finally {
						closeLoadingContext()
					}
				},
			})
		},
		[closeLoadingContext, openError, openLoadingContext, openModal, refreshData],
	)

	const openReplyModal = useCallback((report: any) => {
		if (!report?.id) {
			openModal({ type: 'error', label: 'Report ID is missing' })
			return
		}
		if (!report?.email) {
			openModal({ type: 'error', label: 'Report email is not available' })
			return
		}
		setReplyReport(report)
		setReplyForm({
			subject: `Re: Report ${shortId(report?.id)}`,
			body: '',
		})
	}, [openModal])

	const submitReply = useCallback(async () => {
		const subject = replyForm.subject.trim()
		const body = replyForm.body.trim()
		if (!replyReport?.id) {
			openModal({ type: 'error', label: 'Report ID is missing' })
			return
		}
		if (!replyReport?.email) {
			openModal({ type: 'error', label: 'Report email is not available' })
			return
		}
		if (!subject || !body) {
			openModal({ type: 'error', label: 'Subject and body are required' })
			return
		}
		openLoadingContext()
		try {
			await replyReportEmail(replyReport.id, { subject, body })
			setReplyReport(null)
			await refreshData()
			openModal({ type: 'success', label: 'Reply email sent' })
		} catch (error) {
			openError(error)
		} finally {
			closeLoadingContext()
		}
	}, [
		closeLoadingContext,
		openError,
		openLoadingContext,
		openModal,
		refreshData,
		replyForm,
		replyReport,
	])

	const openBlockModal = useCallback((report: any) => {
		if (!report?.id) {
			openModal({ type: 'error', label: 'Report ID is missing' })
			return
		}
		setBlockReport(report)
		setBlockForm({
			reason: `Blocked from report ${shortId(report?.id)}`,
			type: 'FOREVER',
			target_type: 'target',
		})
	}, [openModal])

	const openNoteModal = useCallback((report: any) => {
		if (!report?.id) {
			openModal({ type: 'error', label: 'Report ID is missing' })
			return
		}
		setNoteReport(report)
		setNoteForm({ note: report?.note || '' })
	}, [openModal])

	const submitNote = useCallback(async () => {
		if (!noteReport?.id) {
			openModal({ type: 'error', label: 'Report ID is missing' })
			return
		}
		openLoadingContext()
		try {
			const res = await updateReportNote(noteReport.id, {
				note: noteForm.note,
			})
			const updatedNote =
				res?.data?.results?.object?.note ||
				res?.data?.results?.objects?.note ||
				noteForm.note
			setData((prev) =>
				prev.map((item) =>
					item.id === noteReport.id
						? {
								...item,
								note: updatedNote,
								last_action: {
									...(item.last_action || {}),
									type: 'update_note',
								},
						  }
						: item,
				),
			)
			setDetailReport((prev) =>
				prev?.id === noteReport.id
					? {
							...prev,
							note: updatedNote,
					  }
					: prev,
			)
			setNoteReport(null)
			await refreshData()
			openModal({ type: 'success', label: 'Report note updated' })
		} catch (error) {
			openError(error)
		} finally {
			closeLoadingContext()
		}
	}, [
		closeLoadingContext,
		noteForm.note,
		noteReport,
		openError,
		openLoadingContext,
		openModal,
		refreshData,
	])

	const submitBlock = useCallback(async () => {
		const reason = blockForm.reason.trim()
		if (!blockReport?.id) {
			openModal({ type: 'error', label: 'Report ID is missing' })
			return
		}
		if (!reason) {
			openModal({ type: 'error', label: 'Block reason is required' })
			return
		}
		openLoadingContext()
		try {
			await blockReportTarget(blockReport.id, {
				...blockForm,
				reason,
			})
			setBlockReport(null)
			await refreshData()
			openModal({ type: 'success', label: 'Target blocked' })
		} catch (error) {
			openError(error)
		} finally {
			closeLoadingContext()
		}
	}, [
		blockForm,
		blockReport,
		closeLoadingContext,
		openError,
		openLoadingContext,
		openModal,
		refreshData,
	])

	const renderActions = useCallback(
		(row) => {
			const report = row.original
			const contentDeletedAt = getReportContentDeletedAt(report)
			const isContentDeleted = isReportContentDeleted(report)
			const deleteContentTooltip = contentDeletedAt
				? `Content was deleted at ${dayjs(contentDeletedAt).format(formatDateTime)}`
				: 'Content was deleted'

			return (
				<Group spacing={6} noWrap>
					<CustomTooltip label="View detail">
						<ActionIcon
							variant="default"
							onClick={() => setDetailReport(row.original)}
						>
							<IconEye size={16} />
						</ActionIcon>
					</CustomTooltip>
					<Menu shadow="md" width={190} withinPortal position="bottom-end">
						<Menu.Target>
							<ActionIcon variant="default">
								<IconDotsVertical size={16} />
							</ActionIcon>
						</Menu.Target>
						<Menu.Dropdown>
							<Menu.Item
								icon={<IconEye size={14} />}
								onClick={() => setDetailReport(row.original)}
							>
								View detail
							</Menu.Item>
							<Menu.Item
								icon={<IconPencil size={14} />}
								onClick={() => openNoteModal(row.original)}
							>
								Update note
							</Menu.Item>
							<Menu.Item
								icon={<IconMail size={14} />}
								disabled={!row.original?.email}
								onClick={() => openReplyModal(row.original)}
							>
								Reply email
							</Menu.Item>
								<Menu.Item
									icon={<IconCircleDot size={14} />}
									onClick={() => handleStatus(row.original, 'in_review')}
								>
									Mark Following
								</Menu.Item>
								<Menu.Item
									icon={<IconCheck size={14} />}
									onClick={() => handleStatus(row.original, 'resolved')}
								>
									Mark Done
								</Menu.Item>
								<Menu.Item
									icon={<IconX size={14} />}
									onClick={() => handleReject(row.original)}
								>
									Mark reject
								</Menu.Item>
								<Menu.Item
									icon={<IconBan size={14} />}
									color="red"
								onClick={() => openBlockModal(row.original)}
							>
								Block target
							</Menu.Item>
							{canDeleteReportContent(report) && (
								<CustomTooltip
									label={deleteContentTooltip}
									disabled={!isContentDeleted}
								>
									<Menu.Item
										icon={<IconTrash size={14} />}
										color="red"
										disabled={isContentDeleted}
										onClick={
											isContentDeleted
												? undefined
												: () => handleDeleteContent(report)
										}
									>
										Delete content
									</Menu.Item>
								</CustomTooltip>
							)}
							</Menu.Dropdown>
					</Menu>
				</Group>
			)
		},
		[
			handleDeleteContent,
			handleReject,
			handleStatus,
			openBlockModal,
			openNoteModal,
			openReplyModal,
		],
	)

	const columns = useMemo<MRT_ColumnDef[]>(
		() => [
			{
				accessorKey: 'created_at',
				header: 'Date',
				size: 110,
				Cell: ({ row }) => {
					const report = row.original as any
					return (
						<Stack spacing={2}>
							<Text size="sm" fw={600}>
								{report?.created_at
									? dayjs(report.created_at).format('MMM DD, YYYY')
									: '-'}
							</Text>
							<Text size="xs" color="dimmed">
								{report?.created_at
									? dayjs(report.created_at).format('HH:mm')
									: '-'}
							</Text>
						</Stack>
					)
				},
			},
			{
				accessorKey: 'reporter',
				header: 'Reporter',
				size: 190,
				Cell: ({ row }) => {
					const report = row.original as any
					return (
						<LinkedText
							title={report?.user?.name || '-'}
							subtitle={shortId(report?.id)}
							avatar={report?.user?.avatar}
							to={report?.user?.id ? `/users/${report.user.id}` : ''}
						/>
					)
				},
			},
			{
				accessorKey: 'email',
				header: 'Email',
				size: 220,
				Cell: ({ row }) => (
					<TruncatedText value={(row.original as any)?.email} maxWidth={210} />
				),
			},
			{
				accessorKey: 'target',
				header: 'Target Reported',
				size: 230,
				Cell: ({ row }) => (
					<LinkedText
						title={getTargetName(row.original)}
						subtitle={getTargetSubtitle(row.original)}
						avatar={(row.original as any)?.target?.avatar}
						to={getTargetRoute(row.original)}
					/>
				),
			},
			{
				accessorKey: 'target_scope',
				header: 'Scope',
				size: 145,
				Cell: ({ row }) => {
					const report = row.original as any
					const scope = getReportTargetScope(report)
					return (
						<Badge radius="sm" variant="light" color={getScopeColor(scope)}>
							{getReportTargetScopeLabel(report)}
						</Badge>
					)
				},
			},
			{
				accessorKey: 'issue_type',
				header: 'Issue',
				size: 170,
				Cell: ({ row }) => (
					<Badge radius="sm" variant="light" color="blue">
						{humanize((row.original as any)?.issue_type)}
					</Badge>
				),
			},
			{
				accessorKey: 'content',
				header: 'Content Preview',
				size: 290,
				Cell: ({ row }) => (
					<TruncatedText value={(row.original as any)?.content} maxWidth={280} />
				),
			},
			{
				accessorKey: 'status',
				header: 'Status',
				size: 130,
				Cell: ({ row }) => {
					const meta = getStatusMeta((row.original as any)?.status)
					return (
						<Badge
							radius="sm"
							sx={{
								background: meta.background,
								color: meta.text,
								textTransform: 'none',
							}}
						>
							{meta.label}
						</Badge>
					)
				},
			},
			{
				accessorKey: 'note',
				header: 'Note',
				size: 220,
				Cell: ({ row }) => (
					<TruncatedText value={(row.original as any)?.note} maxWidth={210} />
				),
			},
			{
				accessorKey: 'action',
				header: 'Action',
				size: ACTION_COLUMN_WIDTH,
				minSize: ACTION_COLUMN_WIDTH,
				maxSize: ACTION_COLUMN_WIDTH,
				mantineTableHeadCellProps: getPinnedReportHeadColumnProps(
					SEND_EMAIL_COLUMN_WIDTH,
					true,
				),
				mantineTableBodyCellProps: getPinnedReportColumnProps(
					SEND_EMAIL_COLUMN_WIDTH,
					true,
				),
				Cell: ({ row }) => renderActions(row),
			},
			{
				accessorKey: 'reply',
				header: 'Send Email',
				size: SEND_EMAIL_COLUMN_WIDTH,
				minSize: SEND_EMAIL_COLUMN_WIDTH,
				maxSize: SEND_EMAIL_COLUMN_WIDTH,
				mantineTableHeadCellProps: getPinnedReportHeadColumnProps(0),
				mantineTableBodyCellProps: getPinnedReportColumnProps(0),
				Cell: ({ row }) =>
					!(row.original as any)?.email ? (
						<Text size="sm" color="dimmed">-</Text>
					) : (
						<Button
							size="xs"
							variant="light"
							leftIcon={<IconMail size={14} />}
							onClick={() => openReplyModal(row.original)}
						>
							Reply
						</Button>
					),
			},
			],
			[openReplyModal, renderActions],
		)

	return (
		<Stack sx={{ width: '100%' }} spacing={16}>
			<Text fw={800} size={28} color="#0f172a">
				Report Tickets
			</Text>

			<Card withBorder radius="md" p="md" sx={{ overflow: 'visible' }}>
				<Flex
					gap={12}
					align="end"
					wrap="nowrap"
					sx={{ overflowX: 'auto', overflowY: 'visible', paddingBottom: 2 }}
				>
					<CustomSelect
						{...FILTER_SELECT_DROPDOWN_PROPS}
						data={SEARCH_FIELD_OPTIONS}
						value={filter.search_field}
						onChange={(value) =>
							setFilter((prev) => ({
								...prev,
								search_field: value || 'reporter_name',
							}))
						}
						sx={{ minWidth: 220, width: 220 }}
					/>
					<CustomClearInput
						value={filter.search_value}
						onChange={(value) =>
							setFilter((prev) => ({ ...prev, search_value: value }))
						}
						placeholder={
							SEARCH_PLACEHOLDER_MAP[filter.search_field] || 'Search...'
						}
						sx={{ minWidth: 300, flex: 1 }}
					/>
					<CustomSelect
						{...FILTER_SELECT_DROPDOWN_PROPS}
						label="Issue Type"
						data={ISSUE_OPTIONS}
						value={filter.issue_type}
						onChange={(value) =>
							setFilter((prev) => ({ ...prev, issue_type: value || 'all' }))
						}
						sx={{ minWidth: 180, width: 180 }}
					/>
					<CustomSelect
						{...FILTER_SELECT_DROPDOWN_PROPS}
						label="Status"
						data={STATUS_OPTIONS}
						value={filter.status}
						onChange={(value) =>
							setFilter((prev) => ({ ...prev, status: value || 'all' }))
						}
						sx={{ minWidth: 150, width: 150 }}
					/>
					<CustomSelect
						{...FILTER_SELECT_DROPDOWN_PROPS}
						label="Sort By"
						data={SORT_OPTIONS}
						value={filter.sort_by}
						onChange={(value) =>
							setFilter((prev) => ({ ...prev, sort_by: value || 'newest' }))
						}
						sx={{ minWidth: 160, width: 160 }}
					/>
				</Flex>
			</Card>

			<Grid gutter="md">
				{[
					['pending', stats.pending],
					['in_review', stats.in_review],
					['resolved', stats.resolved],
					['rejected', stats.rejected],
				].map(([key, value]) => {
					const meta = getStatusMeta(key as string)
					return (
						<Grid.Col span={3} key={key as string}>
							<Card
								withBorder
								radius="md"
								p="md"
								sx={{
									background: meta.background,
									borderColor: meta.background,
								}}
							>
								<Text fw={700} size="sm" color={meta.text}>
									{meta.label}
								</Text>
								<Text fw={800} size={28} color="#0f172a">
									{value as number}
								</Text>
							</Card>
						</Grid.Col>
					)
				})}
			</Grid>

			<TableComponent
				columns={columns}
				total={total}
				data={data}
				pagination={pagination}
				loading={loading}
				handleChangePage={handleChangePage}
				// searchQueryValue={filter.search_value}
				// onSearchQueryInit={handleSearchQueryInit}
				// filterQueryValues={{
				// 	search_field: filter.search_field,
				// 	status: filter.status !== 'all' ? filter.status : undefined,
				// 	issue_type: filter.issue_type !== 'all' ? filter.issue_type : undefined,
				// 	sort_by: filter.sort_by !== 'newest' ? filter.sort_by : undefined,
				// }}
				onFilterQueryInit={handleFilterQueryInit}
				enableRowActions={false}
			/>

			<Modal
				opened={!!detailReport}
				onClose={() => setDetailReport(null)}
				title="Report detail"
				size="lg"
			>
				{detailReport && (
					(() => {
						const target = detailReport.target || {}
						const submittedMedia = getSubmittedMedia(detailReport)
						const targetPreviewImage = getTargetPreviewMediaUrl(target)
						const targetMedia = getTargetMedia(target)
						const targetWebUrl = getTargetWebUrl(detailReport)

						return (
							<Stack spacing={12}>
								<DetailSection title="Ticket summary">
									<Group position="apart">
										<Text fw={800}>{shortId(detailReport.id)}</Text>
										<Badge color={getStatusMeta(detailReport.status).color}>
											{getStatusMeta(detailReport.status).label}
										</Badge>
									</Group>
									<Grid>
										<Grid.Col span={6}>
											<DetailRow
												label="Created"
												value={
													detailReport.created_at
														? dayjs(detailReport.created_at).format(formatDateTime)
														: '-'
												}
											/>
										</Grid.Col>
										<Grid.Col span={6}>
											<DetailRow
												label="Updated"
												value={
													detailReport.updated_at
														? dayjs(detailReport.updated_at).format(formatDateTime)
														: '-'
												}
											/>
										</Grid.Col>
									</Grid>
									<DetailRow label="Issue" value={humanize(detailReport.issue_type)} />
								</DetailSection>

								<DetailSection title="User report content">
									<Group spacing={10} noWrap>
										<Avatar
											src={detailReport?.user?.avatar || undefined}
											radius="xl"
											size={42}
											color="gray"
										>
											{getAvatarLabel(detailReport?.user?.name)}
										</Avatar>
										<Stack spacing={2}>
											<Text fw={700} size="sm">
												{detailReport?.user?.name || '-'}
											</Text>
											<Text size="xs" color="dimmed">
												{detailReport?.email || detailReport?.user?.email || '-'}
											</Text>
										</Stack>
									</Group>
									<DetailRow label="Reporter user ID" value={detailReport.user_id} />
									<DetailRow label="Report content" value={detailReport.content} />
									{submittedMedia.length > 0 && (
											<Stack spacing={6}>
												<Text fw={700} size="sm">Submitted media</Text>
												<Group spacing={10}>
													{submittedMedia.map((media) => (
														<button
															key={media.url}
															type="button"
															onClick={() => setPreviewMedia(media)}
															style={{
																width: 92,
																height: 92,
																border: '1px solid #e2e8f0',
																borderRadius: 8,
																overflow: 'hidden',
																display: 'inline-flex',
																alignItems: 'center',
																justifyContent: 'center',
																background: '#f8fafc',
																cursor: 'zoom-in',
																padding: 0,
															}}
														>
															{media.type === 'VIDEO' ? (
																<Flex direction="column" align="center" gap={4}>
																	<IconPlayerPlay size={28} />
																	<Text size="xs">Video</Text>
																</Flex>
															) : (
																<img
																	src={media.url}
																	alt="Report media"
																	style={{ width: '100%', height: '100%', objectFit: 'cover' }}
																/>
															)}
														</button>
													))}
												</Group>
											</Stack>
										)}
								</DetailSection>

								<DetailSection title="Reported target content">
									<Group align="flex-start" spacing={12} noWrap>
										<Avatar
											src={targetPreviewImage || undefined}
											radius="md"
											size={84}
											color="gray"
										>
											{getAvatarLabel(getTargetName(detailReport))}
										</Avatar>
										<Stack spacing={4} sx={{ flex: 1 }}>
											<Text fw={800} size="sm">
												{getTargetName(detailReport)}
											</Text>
											<Text size="xs" color="dimmed">
												{detailReport.report_target_id || '-'}
											</Text>
											<Group spacing={8}>
												{target?.share_link ? (
													<Button
														component="a"
														href={target.share_link}
														target="_blank"
														rel="noreferrer noopener"
														size="xs"
														variant="light"
														sx={{ width: 'fit-content' }}
													>
														Open App
													</Button>
												) : (
													<Text size="xs" color="dimmed">
														Open App: -
													</Text>
												)}
												{targetWebUrl && (
													<Button
														component="a"
														href={targetWebUrl}
														target="_blank"
														rel="noreferrer noopener"
														size="xs"
														variant="light"
														sx={{ width: 'fit-content' }}
													>
														Open Web
													</Button>
												)}
											</Group>
										</Stack>
									</Group>
									<DetailRow label="Target ID" value={detailReport.report_target_id} />
									<DetailRow
										label="Scope"
										value={getReportTargetScopeLabel(detailReport)}
									/>
									<DetailRow
										label="Content type"
										value={getReportContentTypeLabel(detailReport)}
									/>
									<DetailRow label="Target subtitle" value={getTargetSubtitle(detailReport)} />
									{targetMedia.length > 0 && (
										<Stack spacing={6}>
											<Text fw={700} size="sm">Target media</Text>
											<Group spacing={10}>
												{targetMedia.map((media) => (
													<button
														key={media.url}
														type="button"
														onClick={() => setPreviewMedia(media)}
														style={{
															width: 92,
															height: 92,
															border: '1px solid #e2e8f0',
															borderRadius: 8,
															overflow: 'hidden',
															display: 'inline-flex',
															alignItems: 'center',
															justifyContent: 'center',
															background: '#f8fafc',
															cursor: 'zoom-in',
															padding: 0,
														}}
													>
														{media.type === 'VIDEO' ? (
															<Flex direction="column" align="center" gap={4}>
																<IconPlayerPlay size={28} />
																<Text size="xs">Video</Text>
															</Flex>
														) : (
															<img
																src={media.url}
																alt="Target media"
																style={{
																	width: '100%',
																	height: '100%',
																	objectFit: 'cover',
																}}
															/>
														)}
													</button>
												))}
											</Group>
										</Stack>
									)}
								</DetailSection>

								<DetailSection title="Admin updates">
									<DetailRow label="Note" value={detailReport.note} />
									{detailReport.reply_email && (
										<Stack spacing={6}>
											<Text fw={700} size="sm">Latest reply email</Text>
											<DetailRow
												label="Subject"
												value={detailReport.reply_email.subject}
											/>
											<DetailRow label="Body" value={detailReport.reply_email.body} />
											<DetailRow
												label="Sent at"
												value={
													detailReport.reply_email.sent_at
														? dayjs(detailReport.reply_email.sent_at).format(formatDateTime)
														: '-'
												}
											/>
											<DetailRow
												label="Sent by"
												value={detailReport.reply_email.sent_by}
											/>
										</Stack>
									)}
									{detailReport.last_action && (
										<Stack spacing={6}>
											<Text fw={700} size="sm">Latest action</Text>
											<DetailRow
												label="Type"
												value={humanize(detailReport.last_action.type)}
											/>
											<DetailRow
												label="Status"
												value={humanize(detailReport.last_action.status)}
											/>
											<DetailRow
												label="Reason"
												value={detailReport.last_action.reason}
											/>
											<DetailRow
												label="Created at"
												value={
													detailReport.last_action.created_at
														? dayjs(detailReport.last_action.created_at).format(formatDateTime)
														: '-'
												}
											/>
											<DetailRow
												label="Created by"
												value={detailReport.last_action.created_by}
											/>
										</Stack>
									)}
								</DetailSection>
							</Stack>
						)
					})()
				)}
			</Modal>

			<Modal
				opened={!!previewMedia}
				onClose={() => setPreviewMedia(null)}
				title="Media preview"
				size="xl"
			>
				{previewMedia && (
					<Stack spacing={12}>
						{previewMedia.type === 'VIDEO' ? (
							<video
								src={previewMedia.url}
								poster={previewMedia.thumbnail}
								controls
								style={{ width: '100%', maxHeight: '70vh', borderRadius: 8, background: '#000' }}
							/>
						) : (
							<img
								src={previewMedia.url}
								alt="Report media preview"
								style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8, background: '#f8fafc' }}
							/>
						)}
						<Flex justify="flex-end">
							<Button
								component="a"
								href={previewMedia.url}
								target="_blank"
								rel="noreferrer"
								variant="light"
							>
								Open original
							</Button>
						</Flex>
					</Stack>
				)}
			</Modal>

			<Modal
				opened={!!replyReport}
				onClose={() => setReplyReport(null)}
				title="Reply email"
				size="lg"
			>
				<Stack>
					<Text size="sm" color="dimmed">
						To: {replyReport?.email || '-'}
					</Text>
						<TextInput
							label="Subject"
							value={replyForm.subject}
							onChange={(event) => {
								const value = event.currentTarget.value
								setReplyForm((prev) => ({ ...prev, subject: value }))
							}}
						/>
						<Textarea
							label="Body"
							minRows={8}
							value={replyForm.body}
							onChange={(event) => {
								const value = event.currentTarget.value
								setReplyForm((prev) => ({ ...prev, body: value }))
							}}
						/>
					<Flex justify="flex-end" gap={8}>
						<Button variant="default" onClick={() => setReplyReport(null)}>
							Cancel
						</Button>
							<Button
								onClick={submitReply}
								disabled={!replyForm.subject.trim() || !replyForm.body.trim()}
							>
								Send
							</Button>
					</Flex>
				</Stack>
			</Modal>

			<Modal
				opened={!!noteReport}
				onClose={() => setNoteReport(null)}
				title="Update note"
				size="md"
			>
				<Stack>
					<Text size="sm" color="dimmed">
						{shortId(noteReport?.id)} - {getTargetName(noteReport)}
					</Text>
					<Textarea
						label="Note"
						minRows={6}
						value={noteForm.note}
						onChange={(event) => {
							const value = event.currentTarget.value
							setNoteForm({ note: value })
						}}
					/>
					<Flex justify="flex-end" gap={8}>
						<Button variant="default" onClick={() => setNoteReport(null)}>
							Cancel
						</Button>
						<Button onClick={submitNote}>Save</Button>
					</Flex>
				</Stack>
			</Modal>

			<Modal
				opened={!!blockReport}
				onClose={() => setBlockReport(null)}
				title="Block report target"
				size="md"
			>
				<Stack>
					<Select
						label="Target"
						data={[
							{ label: 'Reported target', value: 'target' },
							{ label: 'Reporter', value: 'reporter' },
						]}
						value={blockForm.target_type}
						onChange={(value: 'target' | 'reporter') =>
							setBlockForm((prev) => ({ ...prev, target_type: value || 'target' }))
						}
					/>
					<Select
						label="Block duration"
						data={BLOCK_TYPE_OPTIONS}
						value={blockForm.type}
						onChange={(value) =>
							setBlockForm((prev) => ({ ...prev, type: value || 'FOREVER' }))
						}
					/>
						<Textarea
							label="Reason"
							minRows={4}
							value={blockForm.reason}
							onChange={(event) => {
								const value = event.currentTarget.value
								setBlockForm((prev) => ({ ...prev, reason: value }))
							}}
						/>
					<Flex justify="flex-end" gap={8}>
						<Button variant="default" onClick={() => setBlockReport(null)}>
							Cancel
						</Button>
							<Button
								color="red"
								onClick={submitBlock}
								disabled={!blockForm.reason.trim()}
							>
								Block
							</Button>
					</Flex>
				</Stack>
			</Modal>
		</Stack>
	)
}

export default React.memo(ReportPage)
